import React from 'react';
import { Alert, Box, Button, Icons, palette, Stack, Text } from '@solvhealth/jigsaw-v1';
import { useTranslation } from 'react-i18next';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { breakpoints } from '~/core/util/styledComponents';
import {
  getGoogleDirectionsUrl,
  isByAppointmentOnly,
  isClosedLocation,
  isTelemedLocation,
} from '~/core/util/location';
import { analyticsTrackEvent } from '~/core/analytics';
import { CLINIC_DETAIL_PAGE_GET_DIRECTIONS_CLICK } from '~/core/analytics/events';
import dataTestIds from '~/test/test-ids';
import { useClinicStatus } from '~/hooks/useClinicStatus';
import LegacyAlert from '~/components/SolvPatternLibrary/Icons/Alert';
import { colors } from '~/constants/colors';

type Props = {
  location: LocationInterface;
};

const PartnerNotBookable = (props: Props) => {
  const { location } = props;
  const isAppointmentOnly = isByAppointmentOnly(location);
  const mdBreakPoint = `@media (min-width: ${breakpoints.md})`;
  const locationHoursStatus = useClinicStatus(location)?.type;
  const { t } = useTranslation();
  const clinicClosedText = t('bookingTimeSelector.slotSelectorStates.clinicClosed.clinicClosed');
  const clinicClosedSubText = t(
    'bookingTimeSelector.slotSelectorStates.clinicClosed.thisClinicIsClosedToday'
  );

  if (isTelemedLocation(location)) {
    return (
      <Box data-testid={dataTestIds.cdp.nonBookableBanner} width={{ _: '90%', md: '100%' }}>
        <Alert
          icon={<Icons.AlertCircle />}
          show
          tone={{
            custom: {
              background: 'gray-100',
              muted: 'gray-400',
              text: 'gray-500',
            },
          }}
        >
          No available visits to book on Solv at this location
        </Alert>
      </Box>
    );
  }

  const handleOnClick = () => {
    window.open(getGoogleDirectionsUrl(location));
    analyticsTrackEvent(CLINIC_DETAIL_PAGE_GET_DIRECTIONS_CLICK, {
      locationId: location.id,
      section: 'non-bookable-partner-section',
      uclId: location.uclId,
    });
  };

  return (
    <Stack
      boxShadow={{ _: 'none', md: 'sm' }}
      data-testid={dataTestIds.cdp.nonBookableBanner}
      space={2}
      sx={{ [mdBreakPoint]: { borderRadius: 'lg' } }}
    >
      <Box
        borderBottom={`1px ${palette.gray[200]} solid`}
        borderTopLeftRadius="xl"
        borderTopRightRadius="xl"
        display={{ _: 'none', md: 'block' }}
        pb={2}
        pt={3}
        style={{ backgroundColor: '#fffdfe' }}
        textColor="gray-600"
      >
        <Text m="0 auto" variant="h2" width="90%">
          Book a visit
        </Text>
      </Box>
      <Stack p={{ _: 0, md: 2 }} px={{ _: 1, md: 3 }} space={2}>
        {isClosedLocation(locationHoursStatus) && (
          <Stack alignItems="center" sx={{ backgroundColor: 'white', borderRadius: 'lg' }}>
            <Stack alignItems="center" row>
              <Box>
                <LegacyAlert />
              </Box>
              <Text color={colors.ladyInRed} display="inline" variant="body2">
                {clinicClosedText}
              </Text>
            </Stack>
            <Box>
              <Text color="gray-500" variant="caption">
                {clinicClosedSubText}
              </Text>
            </Box>
          </Stack>
        )}
        {!isClosedLocation(locationHoursStatus) && (
          <>
            <Box p={2} sx={{ backgroundColor: 'gray-100', borderRadius: 'lg' }}>
              <Text variant="body2">
                This location is not available for online bookings on Solv at this time.
                {!isAppointmentOnly && 'They may be accepting walk-ins.'}
              </Text>
            </Box>
            <Button
              color="primary"
              fullWidth
              mb={2}
              onClick={handleOnClick}
              size="medium"
              variant="outlined"
            >
              Get directions
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default PartnerNotBookable;
